import { Button, FileButton, Group, Loader, Stack, Text, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { FolderUp, LibraryBig, Upload, X } from "lucide-react";
import { useEffect, useRef, useState, type DragEvent } from "react";

import { getListAssetsQueryKey } from "../../../api/generated/endpoints/assets/assets";
import {
  completeActorUpload,
  getListActorsQueryKey,
  moveLibraryImageToActor,
  prepareActorUpload,
} from "../../../api/generated/endpoints/generation/generation";
import type { AiActor, AiActorMime } from "../../../api/generated/model";
import { AiActorMime as ACTOR_MIMES } from "../../../api/generated/zod";
import { refreshTeamStorage } from "../../../api/team-storage";
import { sendToBucket } from "../../../api/upload";
import { ErrorAlert } from "../../ErrorAlert";
import type { PickedImage } from "../../media/ImagePicker";
import { ReferencesDialog } from "../../media/ReferencesDialog";
import { GlassPanel } from "../../../ui/GlassPanel";
import { maximumFiles, selectUpTo } from "../../../ui/format";
import classes from "./composer.module.css";
import type { ActorFormProps } from "./GenerateWithText";

/**
 * Live's limit: one to three actors at a time (`UPLOAD_ACTOR_MAX_REFERENCES`); Talking Head's
 * place sets its own (`TALKING_HEAD_UPLOAD_MAX_REFERENCES`, 1).
 */
const MOST = 3;
const TYPES: readonly string[] = ACTOR_MIMES.options;

/** A picked file on its way: sent to the bucket at once, made an actor on "Add Actors". */
interface Upload {
  from: "upload";
  key: string;
  name: string;
  file: File;
  url: string;
  /** The server's actor once its file is in the bucket; null while it goes up. */
  actorId: string | null;
  failed: unknown;
}

/** A library image picked in "Add From Library": moved into an actor on "Add Actors". */
interface Pick {
  from: "library";
  key: string;
  name: string;
  assetId: string;
  url: string | null;
  failed: unknown;
}

type Taken = Upload | Pick;

/**
 * Live's Upload Actor (`upload-actor`, captures 06 and 07, G8-team-actors): the dashed zone with
 * "Upload or Add AI Actors" and "Recommended aspect ratio 9:16", "Upload Actor" (or files dropped
 * on it) and "Add From Library" (live's library dialog for the room the uploads leave, #237).
 * One to three PNG, JPEG or WebP images, uploaded at once or picked from the library, each shown
 * with its x; "Add Actors", grey until every upload is up, makes them the team's actors (gender
 * Other, as live's): an upload is completed, a library image moved (it leaves the library), each
 * on its own, as live's, so a refused one stays with its words while the rest are made. In
 * Talking Head the place takes them into its cast, and live's Cancel closes the form.
 */
export function UploadActor({ teamId, brandId, above, place, inline }: ActorFormProps) {
  const most = place.uploadMost ?? MOST;
  const queryClient = useQueryClient();
  const [taken, setTaken] = useState<Taken[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [over, setOver] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [library, setLibrary] = useState(false);
  const resetChooser = useRef<() => void>(null);
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach((url) => URL.revokeObjectURL(url)), []);
  const room = most - taken.length;
  const uploads = taken.filter((each): each is Upload => each.from === "upload");
  const picks = taken.filter((each): each is Pick => each.from === "library");
  // Live's library room: what the uploads leave (`libraryMaxCount`).
  const libraryMost = most - uploads.length;
  const send = async (item: Upload) => {
    try {
      const mime = item.file.type as AiActorMime;
      const ticket = await prepareActorUpload(teamId, brandId, { mime, bytes: item.file.size });
      await sendToBucket(ticket, item.file);
      setTaken((current) =>
        current.map((each) => (each.key === item.key ? { ...each, actorId: ticket.actor_id } : each)),
      );
    } catch (error) {
      setTaken((current) =>
        current.map((each) => (each.key === item.key ? { ...each, failed: error } : each)),
      );
    }
  };
  // Live's checks on the files picked or dropped, in its words (`validateFiles`).
  const take = (files: File[]) => {
    setProblem(null);
    if (files.length === 0) return;
    if (files.length > room) {
      setProblem(maximumFiles(room));
      return;
    }
    if (files.some((file) => !TYPES.includes(file.type))) {
      setProblem(`Some files have unsupported file types ${TYPES.join(",")}`);
      return;
    }
    const items = files.map((file): Upload => {
      const url = URL.createObjectURL(file);
      urls.current.push(url);
      const key = `${file.name}-${url}`;
      return { from: "upload", key, name: file.name, file, url, actorId: null, failed: null };
    });
    setTaken((current) => [...current, ...items]);
    items.forEach((item) => void send(item));
  };
  // Live's library dialog replaces the picks with what it confirms, after the uploads.
  const confirmPicks = (picked: PickedImage[]) => {
    setLibrary(false);
    const chosen = picked.map(
      (image): Pick => ({
        from: "library",
        key: `library-${image.assetId}`,
        name: image.name,
        assetId: image.assetId,
        url: image.url,
        failed: null,
      }),
    );
    setTaken((current) => [...current.filter((each) => each.from === "upload"), ...chosen]);
  };
  const actorOf = (item: Taken): Promise<AiActor> => {
    if (item.from === "library") {
      return moveLibraryImageToActor(teamId, brandId, { asset_id: item.assetId });
    }
    const mime = item.file.type as AiActorMime;
    return completeActorUpload(teamId, brandId, item.actorId ?? "", { mime });
  };
  // Live's "Add Actors": each on its own (`Promise.allSettled`); the made ones leave the list.
  const add = async () => {
    setAdding(true);
    setFailure(null);
    try {
      const sent = taken.map((item) => ({ item, made: actorOf(item) }));
      const settled = await Promise.allSettled(sent.map((each) => each.made));
      const made: AiActor[] = [];
      const refused = new Map<string, unknown>();
      settled.forEach((outcome, at) => {
        const key = sent[at]!.item.key;
        if (outcome.status === "fulfilled") made.push(outcome.value);
        else refused.set(key, outcome.reason);
      });
      // A refused one stays with the server's words; the made ones leave.
      const keys = new Set(sent.map((each) => each.item.key));
      setTaken((current) =>
        current.flatMap((each) => {
          if (!keys.has(each.key)) return [each];
          return refused.has(each.key) ? [{ ...each, failed: refused.get(each.key) }] : [];
        }),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [getListActorsQueryKey(teamId, brandId)[0]] }),
        // Each actor's portrait counts in the team's storage.
        made.length === 0 ? Promise.resolve() : refreshTeamStorage(queryClient, teamId),
        // A moved image left the library.
        picks.length === 0
          ? Promise.resolve()
          : queryClient.invalidateQueries({ queryKey: [getListAssetsQueryKey(teamId, brandId)[0]] }),
      ]);
      if (made.length > 0) place.onCreated?.(made);
    } catch (error) {
      setFailure(error);
    } finally {
      setAdding(false);
    }
  };
  const ready = taken.length > 0 && uploads.every((each) => each.actorId !== null);
  const drop = {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!event.dataTransfer.types.includes("Files") || room === 0) return;
      event.preventDefault();
      setOver(true);
    },
    onDragLeave: () => setOver(false),
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setOver(false);
      take(Array.from(event.dataTransfer.files));
    },
  };
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      summary={<span>Upload Actor</span>}
      above={above}
    >
      <Stack gap={12}>
        <section
          className={classes.drop}
          data-over={over || undefined}
          data-off={room === 0 || undefined}
          aria-label="Upload or Add AI Actors"
          {...drop}
        >
          <FolderUp size={40} strokeWidth={1.5} aria-hidden />
          <Text fw={500} size="sm">
            Upload or Add AI Actors
          </Text>
          <Text size="xs" c="var(--cai-color-text-muted)">
            Recommended aspect ratio 9:16
          </Text>
          <Group gap={16}>
            <FileButton
              multiple
              accept={TYPES.join(",")}
              resetRef={resetChooser}
              disabled={room === 0 || adding}
              onChange={(files) => {
                resetChooser.current?.();
                take(files);
              }}
              inputProps={{ "aria-label": "Choose actors to upload" }}
            >
              {(props) => (
                <Button {...props} variant="outline" leftSection={<Upload size={16} />}>
                  Upload Actor
                </Button>
              )}
            </FileButton>
            <Button
              leftSection={<LibraryBig size={16} />}
              disabled={room === 0 || adding}
              onClick={() => setLibrary(true)}
            >
              Add From Library
            </Button>
          </Group>
        </section>
        {problem === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {problem}
          </Text>
        )}
        {taken.length === 0 ? null : (
          <Stack gap={12}>
            <div className={classes.thumbs}>
              {taken.map((each) => (
                <span key={each.key} className={classes.thumb}>
                  {each.url === null ? null : (
                    <img src={each.url} alt={each.name} className={classes.thumbPicture} />
                  )}
                  {each.from === "upload" && each.actorId === null && each.failed === null ? (
                    <Loader size={16} className={classes.thumbBusy} aria-label={`Uploading ${each.name}`} />
                  ) : null}
                  <UnstyledButton
                    className={classes.thumbRemove}
                    aria-label={`Remove ${each.name}`}
                    disabled={adding}
                    onClick={() => setTaken((current) => current.filter((had) => had.key !== each.key))}
                  >
                    <X size={12} strokeWidth={2.5} aria-hidden />
                  </UnstyledButton>
                </span>
              ))}
            </div>
            {taken.map((each) =>
              each.failed === null ? null : <ErrorAlert key={each.key} error={each.failed} />,
            )}
            <Button loading={adding} disabled={!ready || adding} onClick={() => void add()}>
              Add Actors
            </Button>
          </Stack>
        )}
        {failure === null ? null : <ErrorAlert error={failure} />}
        {library ? (
          <ReferencesDialog
            teamId={teamId}
            brandId={brandId}
            title="Add From Library"
            subtitle={selectUpTo(libraryMost, "image")}
            max={libraryMost}
            chosen={picks.map((each) => each.assetId)}
            onClose={() => setLibrary(false)}
            onConfirm={confirmPicks}
          />
        ) : null}
        {place.onCancel === undefined ? null : (
          <Group justify="flex-end">
            <Button variant="outline" disabled={adding} onClick={place.onCancel}>
              Cancel
            </Button>
          </Group>
        )}
      </Stack>
    </GlassPanel>
  );
}
